import { useState, useEffect, useRef, useCallback } from 'react';
import { refreshTasksFromApi, syncTasksToApi } from '../services/TasksService';
import { refreshCalendarFromApi, syncCalendarToApi } from '../services/CalendarService';
import { refreshLogisticsVehiclesFromApi, refreshLogisticsDriversFromApi, refreshLogisticsAssignmentsFromApi, syncLogisticsToApi } from '../components/LogisticsManager';
import { refreshStationeryFromApi, syncStationeryToApi } from '../components/StationeryTracker';
import { refreshBudgetFromApi } from '../utils/budgetStorage';
import { refreshEventsFromApi } from '../services/EventsService';
import { refreshTeamFromApi } from '../services/TeamService';
import { refreshInboxFromApi } from '../services/CommunicationsService';
import { refreshProcurementFromApi } from '../services/ProcurementService';
import { refreshInventoryFromApi } from '../services/InventoryService';
import { refreshGuestsFromApi } from '../services/GuestService';
import { refreshFabricsFromApi, refreshAssignmentsFromApi } from '../services/AsoebiService';
import { refreshDeliveriesFromApi } from '../services/AsoebiDeliveryService';
import { refreshVenuesFromApi } from '../services/VenueApiService';
import { BUDGET_ENABLED, COMMUNICATIONS_ENABLED, EVENTS_ENABLED, GUESTS_ENABLED, PROCUREMENT_ENABLED, TEAM_ENABLED, ASOEBI_ENABLED, VENUES_ENABLED } from '../constants/featureFlags';

// Vendor bookings and the asoebi packing-assignments checklist are not
// synced here yet: those API routes do not exist, so polling them only
// produced 404s. Budget is refreshed with the rest; it stops asking once the API
// answers 403 (a role that may not see money), see budgetStorage.refreshBudgetFromApi.
const MIGRATION_FLAG = 'anojeje_api_migrated_v1';

/** 30 s while few endpoints are refreshed; 120 s once more than 8 are, so the refresh does not flood the API. */
export function refreshIntervalMs(loaderCount: number): number {
  return loaderCount > 8 ? 120_000 : 30_000;
}

interface PlanningSyncState {
  syncing: boolean;
  lastSyncTime: Date | null;
  syncError: string | null;
  triggerSync: () => Promise<void>;
  triggerMigration: () => Promise<void>;
}

async function runAllSyncToApi(): Promise<void> {
  const results = await Promise.allSettled([
    syncTasksToApi(),
    syncCalendarToApi(),
    syncLogisticsToApi(),
    syncStationeryToApi(),
  ]);

  const failures = results.filter((r) => r.status === 'rejected');
  if (failures.length > 0) {
    console.warn(
      '[usePlanningSync] Some sync-to-API calls failed:',
      failures.map((f) => (f as PromiseRejectedResult).reason),
    );
  }
}

interface RefreshModule {
  name: string;
  /** Build-time flag for the module; the module's routes exist only when it is on. */
  enabled: boolean;
  refresh: () => Promise<unknown>;
}

export interface RefreshFlags {
  events: boolean;
  communications: boolean;
  budget: boolean;
  guests: boolean;
  team: boolean;
  procurement: boolean;
  asoebi: boolean;
  venues: boolean;
}

const DEFAULT_REFRESH_FLAGS: RefreshFlags = {
  events: EVENTS_ENABLED,
  communications: COMMUNICATIONS_ENABLED,
  budget: BUDGET_ENABLED,
  guests: GUESTS_ENABLED,
  team: TEAM_ENABLED,
  procurement: PROCUREMENT_ENABLED,
  asoebi: ASOEBI_ENABLED,
  venues: VENUES_ENABLED,
};

/** The loaders the auto-refresh (and the refresh button) run. A module that is switched off is not polled at all. */
export function refreshModules(flags: RefreshFlags = DEFAULT_REFRESH_FLAGS): RefreshModule[] {
  return [
    { name: 'tasks', enabled: true, refresh: refreshTasksFromApi },
    { name: 'calendar', enabled: true, refresh: refreshCalendarFromApi },
    { name: 'logistics-vehicles', enabled: true, refresh: refreshLogisticsVehiclesFromApi },
    { name: 'logistics-drivers', enabled: true, refresh: refreshLogisticsDriversFromApi },
    { name: 'logistics-movements', enabled: true, refresh: refreshLogisticsAssignmentsFromApi },
    { name: 'stationery', enabled: true, refresh: refreshStationeryFromApi },
    { name: 'events', enabled: flags.events, refresh: refreshEventsFromApi },
    { name: 'communications', enabled: flags.communications, refresh: refreshInboxFromApi },
    { name: 'budget', enabled: flags.budget, refresh: refreshBudgetFromApi },
    { name: 'guests', enabled: flags.guests, refresh: refreshGuestsFromApi },
    // Quiet: a role below admin gets a 403, which clears the cache and is not an error here.
    { name: 'team', enabled: flags.team, refresh: () => refreshTeamFromApi({ quiet: true }) },
    { name: 'procurement-orders', enabled: flags.procurement, refresh: refreshProcurementFromApi },
    { name: 'procurement-inventory', enabled: flags.procurement, refresh: refreshInventoryFromApi },
    { name: 'asoebi-fabrics', enabled: flags.asoebi, refresh: refreshFabricsFromApi },
    { name: 'asoebi-orders', enabled: flags.asoebi, refresh: refreshAssignmentsFromApi },
    { name: 'asoebi-deliveries', enabled: flags.asoebi, refresh: refreshDeliveriesFromApi },
    { name: 'venues', enabled: flags.venues, refresh: refreshVenuesFromApi },
  ].filter(m => m.enabled);
}

const REFRESH_INTERVAL_MS = refreshIntervalMs(refreshModules().length);

/**
 * Every loader the auto-refresh and the refresh button run, in one list —
 * kept as a plain function array (derived from refreshModules()) for
 * callers that only need "run them all", not each module's name/flag.
 */
export const REFRESH_LOADERS: ReadonlyArray<() => Promise<unknown>> = refreshModules().map(m => m.refresh);

// A background refresh never raises a toast: a failure is logged, the data
// already on screen stays, and each screen shows its own error box (with Retry)
// when the person is looking at it.
export async function runAllRefreshFromApi(modules: RefreshModule[] = refreshModules()): Promise<void> {
  const results = await Promise.allSettled(modules.map(m => m.refresh()));

  const failures = results
    .map((r, i) => ({ r, name: modules[i].name }))
    .filter(x => x.r.status === 'rejected');
  if (failures.length > 0) {
    console.warn(
      '[usePlanningSync] Some refresh-from-API calls failed:',
      failures.map(f => `${f.name}: ${String((f.r as PromiseRejectedResult).reason)}`),
    );
  }
}

export function usePlanningSync(): PlanningSyncState {
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const triggerSync = useCallback(async () => {
    if (!mountedRef.current) return;
    setSyncing(true);
    setSyncError(null);
    try {
      await runAllRefreshFromApi();
      if (mountedRef.current) {
        setLastSyncTime(new Date());
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Sync refresh failed';
      if (mountedRef.current) {
        setSyncError(message);
      }
      console.warn('[usePlanningSync] triggerSync error:', error);
    } finally {
      if (mountedRef.current) {
        setSyncing(false);
      }
    }
  }, []);

  const triggerMigration = useCallback(async () => {
    if (!mountedRef.current) return;
    setSyncing(true);
    setSyncError(null);
    try {
      await runAllSyncToApi();
      localStorage.setItem(MIGRATION_FLAG, new Date().toISOString());
      if (mountedRef.current) {
        setLastSyncTime(new Date());
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Migration failed';
      if (mountedRef.current) {
        setSyncError(message);
      }
      console.warn('[usePlanningSync] triggerMigration error:', error);
    } finally {
      if (mountedRef.current) {
        setSyncing(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const bootstrap = async () => {
      try {
        if (cancelled) return;
        const alreadyMigrated = localStorage.getItem(MIGRATION_FLAG);
        if (!alreadyMigrated) {
          await triggerMigration();
        }
        if (cancelled) return;
        await triggerSync();
      } catch (error: unknown) {
        console.warn('[usePlanningSync] bootstrap error:', error);
      }
    };

    bootstrap();

    const intervalId = setInterval(() => {
      if (!cancelled) triggerSync();
    }, REFRESH_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [triggerMigration, triggerSync]);

  return { syncing, lastSyncTime, syncError, triggerSync, triggerMigration };
}
